import React, { useContext } from "react";
import {
  Box,
  Button,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import qs from "query-string";
import { useNavigate, useLocation } from "react-router";
import { isBefore } from "date-fns";
import { useListAndSearchAllSubscriptions } from "@rentablo/api/apiv1";
import { MandatorContext } from "@rentablo/theming";
import AddIcon from "@icons/myicons/plus.svg?react";
import TargetArrow from "@icons/myicons/target-arrow.svg?react";
import { AdaptiveButton, PageLayout, AccountRowLoading } from "@components";
import { Banner, BottomControlsPanel } from "@rentablo/components";
import { Tab, Tabs } from "@rentablo/components";
import { useMyInvoices } from "@api/v1/hooks/useInvoices";
import CashbackDashboardItem from "./components/CashbackDashboardItem";
import { CashbackDashboardSummary } from "./components/CashbackDashboardSummary";
import { CashbackDashboardContacts } from "./components/CashbackDashboardContacts";

const PaddedBox = styled(Box)`
  padding: ${({ theme }) => theme.spacing(3, 0)};
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const ItemWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const TabsWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing(-2)};

  ${({ theme }) => theme.breakpoints.down("sm")} {
    margin-top: ${({ theme }) => theme.spacing(-4)};
  }
`;
const TabPanel = styled.div`
  padding-top: ${({ theme }) => theme.spacing(4)};
`;

const BlueText = styled("span")(({ theme }) => ({
  color: theme.palette.primary.dark,
  fontWeight: 600,
}));

const CashbackDashboardPage = () => {
  const theme = useTheme();
  const {
    data: subscriptionsList,
    isFetching,
    isPending,
  } = useListAndSearchAllSubscriptions({
    isCancelled: false,
  });

  const {
    data: invoiceList,
    isFetching: invoicesLoading,
    isPending: isInvoicePending,
  } = useMyInvoices();
  const { t } = useTranslation();
  const { isOnvista } = useContext(MandatorContext);
  const subscriptions = subscriptionsList?.subscriptions;
  const navigate = useNavigate();
  const location = useLocation();

  const handleTabChange = (key: string) => {
    navigate({ search: qs.stringify({ tab: key }) });
  };

  const defaultTabKey = qs.parse(location.search) as { tab?: string };

  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const isPhone = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const openWizard = () => navigate("/cashback-wizard-start");
  const shouldShowCashbackBonusBanner = !isOnvista;

  return (
    <PageLayout
      isMainPage
      data-testid="cashback-dashboard"
      hasBottomControlsPanel={isPhone}
      title={t("domains.cashback.dashboard.title")}
      breadcrumbs={[
        { to: "/", text: t("breadcrumbs.home") },
        { text: t("breadcrumbs.cashback") },
      ]}
      actionButton={
        isPhone ? undefined : (
          <Button
            onClick={openWizard}
            disableElevation
            variant="outlined"
            startIcon={<AddIcon width="18px" height="12px" />}
          >
            {t("domains.cashback.dashboard.titleAction")}
          </Button>
        )
      }
    >
      <TabsWrapper>
        <Tabs onChange={handleTabChange} defaultKey={defaultTabKey.tab}>
          <Tab
            label={t("domains.cashback.dashboard.tabs.overview")}
            key="overview"
          >
            <TabPanel>
              {shouldShowCashbackBonusBanner && (
                <Banner
                  type="info"
                  icon={<TargetArrow width="20px" height="20px" />}
                  title={t("domains.cashback.dashboard.info.title")}
                  text={
                    <Trans
                      transWrapTextNodes="span"
                      components={{
                        blueText: <BlueText />,
                      }}
                      values={{
                        amount: isBefore(new Date(), new Date("2023-09-29"))
                          ? "50"
                          : "25",
                      }}
                    >
                      {t("domains.cashback.dashboard.info.text")}
                    </Trans>
                  }
                  primaryButton={{
                    label: t("domains.cashback.dashboard.info.recommend"),
                    onClick: () => navigate("/cashback/invite-friend"),
                  }}
                  sx={{ marginBottom: theme.spacing(6) }}
                />
              )}
              <PaddedBox>
                <Typography color="textSecondary" variant="body4">
                  {t("domains.cashback.dashboard.openBookings")}
                </Typography>
              </PaddedBox>
              {isPending || isFetching ? (
                <Box display="flex" flexDirection="column" gap={3}>
                  <AccountRowLoading hideShadow />
                  <AccountRowLoading hideShadow />
                  <AccountRowLoading hideShadow />
                </Box>
              ) : (
                subscriptions?.map((s) => (
                  <ItemWrapper key={s.id}>
                    <CashbackDashboardItem subscription={s} />
                  </ItemWrapper>
                ))
              )}
              {isSm && (
                <Button
                  onClick={() => navigate("/cashback-wizard-start")}
                  size="small"
                  fullWidth
                  variant="outlined"
                  startIcon={<AddIcon width="18px" height="12px" />}
                >
                  {t("domains.cashback.dashboard.titleAction")}
                </Button>
              )}
              <PaddedBox
                sx={{
                  [theme.breakpoints.down("md")]: {
                    paddingTop: theme.spacing(6),
                  },
                }}
              >
                <CashbackDashboardSummary
                  isFetching={isInvoicePending || invoicesLoading}
                  invoiceList={invoiceList}
                  showHeader
                />
              </PaddedBox>
              <Box>
                <Typography variant="body3">
                  {t("domains.cashback.dashboard.footer")}
                </Typography>
              </Box>
            </TabPanel>
          </Tab>
          <Tab
            label={t("domains.cashback.dashboard.tabs.invoices")}
            key="invoices"
          >
            <TabPanel>
              <CashbackDashboardSummary
                invoiceList={invoiceList}
                isFetching={invoicesLoading}
              />
            </TabPanel>
          </Tab>
          <Tab
            label={t("domains.cashback.dashboard.tabs.contacts")}
            key="contacts"
          >
            <TabPanel>
              <CashbackDashboardContacts />
            </TabPanel>
          </Tab>
        </Tabs>
      </TabsWrapper>
      {isPhone && (
        <BottomControlsPanel
          actions={
            <AdaptiveButton
              iconOnly
              icon={<AddIcon width="16px" height="16px" />}
              label={t("domains.cashback.dashboard.titleAction")}
              testId="cashback-add-button"
              onClick={openWizard}
            />
          }
        >
          {null}
        </BottomControlsPanel>
      )}
    </PageLayout>
  );
};

export default CashbackDashboardPage;
